// music-resolver.ts — what a viewer should be hearing, decided in the BROWSER.
//
// Owner direction (2026-09-10, plans/ft3d-client-music-handoff-2026-09-10.md): the back end only SAVES
// music state — the GM's theme or single track (campaign row), the encounter's ambient + combat buckets,
// a room's buckets (encounter_shape `creation.music`) — and publishes it on the WebSocket frames that
// already exist. Choosing the playlist and its running order happens here, from the music catalog
// (music-catalog.ts, one fetch per catalog revision). This replaced ft_resolve_encounter_music() and
// ft_music_creator_program(), which answered a PHP request on every change.
//
// Pure: no network, no DOM. tools/test/music-resolver.test.mjs runs it under node against the golden
// fixture the retired PHP produced (tools/test/fixtures/music-creator-golden.json).

/** One catalog row. Short keys because the catalog carries the whole library (~2.4k tracks). */
export interface FtMusicCatalogTrack {
    id: string;
    /** Player-facing name ("Journey into the Riverlands (B)"), the internal handle when untitled. */
    n: string;
    s?: number;
    /** Present (1) on sung takes. */
    v?: 1;
    /** Present only when the format is not mp3. */
    f?: string;
}

export interface FtMusicCatalog {
    schema: number;
    rev: string;
    cdn: string;
    crossfade_ms: number;
    creator_crossfade_ms: number;
    buckets: Record<string, FtMusicCatalogTrack[]>;
}

export interface FtMusicTrack {
    audio_id: string;
    audio_file: string;
    audio_seconds: number | null;
    // The troubleshooting handle in logs; the catalog ships display names only, so it IS the display
    // name here. display_name stays separate because the player's render path reads both.
    audio_name: string;
    display_name?: string;
    // Creator program only: 'ancestry_instrumental' | 'ancestry_vocal' | 'general'.
    program_slot?: string;
    program_bucket?: string | null;
}

export interface FtMusicResolved {
    mode: string;                 // 'local' | 'override' | 'combat' | 'default' | 'creator' | 'none'
    bucket: string | null;
    order_key: string;
    tracks: FtMusicTrack[];
    crossfade_ms: number;
    track_count: number;
    reason: string;
    // Buckets the saved state named that this catalog copy does not carry at all — an ingest newer
    // than the copy. The player refetches the catalog once and resolves again.
    missing: string[];
    program?: string[];
    ancestry_name?: string;
    ancestry_bucket?: string | null;
    vocal_bucket?: string | null;
}

/**
 * FNV-1a, 32-bit. The running-order hash: every client derives the same order from the same key, so
 * the table hears the same track with no sync traffic (the property the old server-side md5 order
 * had). Changing the key changes the order.
 */
export function ftMusicHash(s: string): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

export function ftMusicTrackFromCatalog(catalog: FtMusicCatalog, row: FtMusicCatalogTrack): FtMusicTrack {
    return {
        audio_id: row.id,
        audio_file: catalog.cdn + row.id + '.' + (row.f || 'mp3'),
        audio_seconds: typeof row.s === 'number' && row.s > 0 ? row.s : null,
        audio_name: row.n,
        display_name: row.n,
    };
}

/**
 * A bucket's playlist in its deterministic order for `orderKey`.
 *
 * Sung takes are excluded unless `includeVocal`: song-form cues wear out under a 20-minute loop, so
 * they stay out of the AUTOMATIC rotation; an explicit pick (a GM theme, "just me", the creator's sung
 * slot) must still play them.
 */
export function ftMusicBucketTracks(
    catalog: FtMusicCatalog, bucket: string, orderKey: string, includeVocal: boolean,
): FtMusicTrack[] {
    const rows = bucket ? catalog.buckets[bucket] : undefined;
    if (!Array.isArray(rows)) return [];
    return rows
        .filter(r => includeVocal || !r.v)
        .map(r => ({ r, k: ftMusicHash(orderKey + '|' + r.id) }))
        .sort((a, b) => a.k - b.k || (a.r.id < b.r.id ? -1 : a.r.id > b.r.id ? 1 : 0))
        .map(x => ftMusicTrackFromCatalog(catalog, x.r));
}

/** Bucket names are MusicForge slugs; anything else in saved state is ignored rather than trusted. */
export function ftMusicBucketName(value: unknown): string {
    const s = typeof value === 'string' ? value.trim() : '';
    return /^[a-z0-9_]{1,64}$/.test(s) ? s : '';
}

/**
 * A room's saved music (`encounter_shape.boxes[].creation.music`). Blank = follow the encounter.
 * Written by options/api/ft3d_room_music_inc.php.
 */
export function ftNormalizeRoomMusic(value: unknown): { bucket: string; combatBucket: string; follow: '' | 'encounter' } {
    const row = value && typeof value === 'object' ? value as Record<string, unknown> : {};
    // A blank field falls back to the room TYPE's default, then the encounter's; follow 'encounter'
    // skips the room-type default and plays the encounter's music.
    return {
        bucket: ftMusicBucketName(row.bucket), combatBucket: ftMusicBucketName(row.combatBucket),
        follow: row.follow === 'encounter' ? 'encounter' : '',
    };
}

/** Everything a viewer's music depends on. Read from the WS-fed caches by music-player.ts. */
export interface FtMusicViewerState {
    encounterId: string;
    inCombat: boolean;
    encounterBucket: string;
    encounterCombatBucket: string;
    roomBucket: string;
    roomCombatBucket: string;
    /** Where each room bucket came from: authored on the room, or its room type's default. */
    roomBucketSource?: '' | 'room' | 'room-type';
    roomCombatSource?: '' | 'room' | 'room-type';
    /** GM theme, pushed campaign-wide (campaign_encounter_mx_bucket). */
    gmBucket: string;
    /** GM single track (campaign_encounter_mx), resolved to its file when the campaign blob was built. */
    gmTrack: FtMusicTrack | null;
    /** "Just me" theme: this viewer only, never saved or broadcast. */
    localBucket: string;
}

/**
 * PRECEDENCE (highest first):
 *   0. local "just me" theme                       -> 'local'     (this viewer only)
 *   1. GM theme                                    -> 'override'  (same order everywhere)
 *   2. GM single track                             -> 'override'  (one looping track)
 *   3. in combat: room combat bucket, else the encounter's            -> 'combat'
 *   4. the room's bucket, else the encounter's default                -> 'default'
 *   5. nothing                                     -> 'none'      (the player KEEPS what is playing)
 *
 * Room and encounter share one order key (`encounterId|bucket`), so walking between two rooms that
 * play the same bucket never restarts the music.
 */
export function ftResolveViewerMusic(state: FtMusicViewerState, catalog: FtMusicCatalog): FtMusicResolved {
    const missing: string[] = [];
    let reason = '';
    const out = (mode: string, bucket: string | null, orderKey: string, tracks: FtMusicTrack[], why: string): FtMusicResolved => ({
        mode, bucket, order_key: orderKey, tracks, crossfade_ms: catalog.crossfade_ms,
        track_count: tracks.length, reason: reason + why, missing,
    });
    const known = (bucket: string): boolean => {
        if (Object.prototype.hasOwnProperty.call(catalog.buckets, bucket)) return true;
        missing.push(bucket);
        return false;
    };

    const local = ftMusicBucketName(state.localBucket);
    if (local) {
        const key = 'local|' + local;
        const tracks = known(local) ? ftMusicBucketTracks(catalog, local, key, true) : [];
        return tracks.length
            ? out('local', local, key, tracks, 'local bucket requested by viewer')
            : out('none', null, key, [], 'local bucket empty/unknown');
    }

    const gmBucket = ftMusicBucketName(state.gmBucket);
    if (gmBucket) {
        const key = 'gm|' + gmBucket;
        const tracks = known(gmBucket) ? ftMusicBucketTracks(catalog, gmBucket, key, true) : [];
        if (tracks.length) return out('override', gmBucket, key, tracks, 'GM theme');
        reason += 'GM theme empty/unknown; fell through. ';
    }

    if (state.gmTrack && state.gmTrack.audio_id && state.gmTrack.audio_file) {
        return out('override', null, 'track|' + state.gmTrack.audio_id, [state.gmTrack], 'GM single track');
    }

    const encounterId = String(state.encounterId || '');
    if (state.inCombat) {
        const combat = ftMusicBucketName(state.roomCombatBucket) || ftMusicBucketName(state.encounterCombatBucket);
        if (combat) {
            const key = encounterId + '|' + combat;
            const tracks = known(combat) ? ftMusicBucketTracks(catalog, combat, key, false) : [];
            return out('combat', combat, key, tracks,
                !ftMusicBucketName(state.roomCombatBucket) ? 'in combat, encounter combat bucket'
                    : state.roomCombatSource === 'room-type' ? 'in combat, room-type combat bucket' : 'in combat, room combat bucket');
        }
    }

    const ambient = ftMusicBucketName(state.roomBucket) || ftMusicBucketName(state.encounterBucket);
    if (ambient) {
        const key = encounterId + '|' + ambient;
        const tracks = known(ambient) ? ftMusicBucketTracks(catalog, ambient, key, false) : [];
        return out('default', ambient, key, tracks,
            !ftMusicBucketName(state.roomBucket) ? 'encounter bucket'
                : state.roomBucketSource === 'room-type' ? 'room-type default' : 'room bucket');
    }

    return out('none', null, '', [], 'no override, no combat bucket, no room or encounter bucket');
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// CHARACTER-CREATOR PROGRAM (port of the retired creator_music_helpers.php, 2026-09-10)
//
// The creator has no encounter. It plays a repeating cycle that pivots between the ancestry's own music
// and the general `character_creator` bucket, alternating sung and instrumental so two vocal tracks never
// run back to back:  [ ancestry instrumental , ancestry vocal , general ].
//
// ⚠ THE BUCKET MAP IS DERIVED, NOT HARDCODED (owner 2026-08-26: "we are adding more music all the time"):
// candidate slugs are spelled out of the ancestry NAME and kept only when the catalog has them, so
// ingesting an `orcish` bucket lights Orc up with no code change. The kinship map is only the fallback
// for names that can never spell their bucket, and any derived bucket outranks it.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

export const FT_MUSIC_CREATOR_BUCKET = 'character_creator';
export const FT_MUSIC_CREATOR_PROGRAM_LEN = 60;

export const FT_MUSIC_ANCESTRY_KIN: Readonly<Record<string, string>> = {
    // Big, loud, tribal — the `barbarian` bucket.
    orc: 'barbarian', minotaur: 'barbarian', ogrefolk: 'barbarian', trollfolk: 'barbarian',
    gnoll: 'barbarian', bugbear: 'barbarian', goblin: 'barbarian', kobold: 'barbarian', giantfolk: 'barbarian',
    // Homely, hearth-side — the tavern buckets (these DO have a sung half).
    halfolk: 'taverns', hobfolk: 'taverns', harvestfolk: 'taverns', grasswalker: 'taverns',
    // Fey kin ride the wood-elf material.
    fairy: 'elvish_wood', satyr: 'elvish_wood', spriggin: 'elvish_wood', flowerfolk: 'elvish_wood',
};

/** Buckets an ancestry NAME may never claim, however its slug spells out (the general + combat beds). */
export const FT_MUSIC_ANCESTRY_RESERVED_BUCKETS: readonly string[] = [
    FT_MUSIC_CREATOR_BUCKET, 'battle', 'battle_epic', 'battle_epic_god_mode', 'battle_forest',
    'battle_scary', 'battle_vampire', 'action', 'urgency', 'victory', 'holiday',
];

/** "Elf, Wood" -> "elf_wood". */
export function ftMusicSlug(s: string): string {
    return String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/** elf -> elvish, dwarf -> dwarvish, orc -> orcish. A guess; every candidate is checked against the catalog. */
export function ftMusicAdjectival(noun: string): string {
    if (noun === '') return '';
    if (noun.endsWith('ish')) return noun;
    if (noun.endsWith('f')) return noun.slice(0, -1) + 'vish';
    if (noun.endsWith('e')) return noun;          // gnome -> gnome ("gnomish" is tried separately)
    return noun + 'ish';
}

/** Candidate bucket slugs for an ancestry name, most specific first. */
export function ftMusicAncestryBucketCandidates(ancestryName: string): string[] {
    const slug = ftMusicSlug(ancestryName);
    if (slug === '') return [];
    // The catalog writes the qualifier SECOND ("Elf, Wood"); the buckets put it first (elvish_wood).
    const parts = slug.split('_').filter(p => p !== '');
    const base = parts[0] ?? '';
    const qual = parts.slice(1).join('_');
    const adj = ftMusicAdjectival(base);
    const out: string[] = [];
    if (qual !== '') out.push(qual + '_' + base, base + '_' + qual, qual + '_' + adj, adj + '_' + qual);
    out.push(slug, adj, base);
    if (base.endsWith('e')) out.push(base.slice(0, -1) + 'ish');
    return [...new Set(out.filter(s => s !== ''))];
}

type FtMusicSplit = Record<string, { instrumental: number; vocal: number }>;

export function ftMusicBucketSplit(catalog: FtMusicCatalog): FtMusicSplit {
    const split: FtMusicSplit = {};
    for (const [bucket, rows] of Object.entries(catalog.buckets)) {
        let vocal = 0;
        for (const r of rows) if (r.v) vocal++;
        split[bucket] = { instrumental: rows.length - vocal, vocal };
    }
    return split;
}

/** The sung bucket partnering an instrumental one: `<bucket>_vocals`, or the singular stem (taverns -> tavern_vocals). */
export function ftMusicVocalPartner(bucket: string, split: FtMusicSplit): string | null {
    if (bucket === '') return null;
    const tries = [bucket + '_vocals'];
    if (bucket.endsWith('s')) tries.push(bucket.slice(0, -1) + '_vocals');
    for (const t of tries) if ((split[t]?.vocal ?? 0) > 0) return t;
    return null;
}

export function ftMusicAncestryBuckets(ancestryName: string, split: FtMusicSplit): {
    instrumental: string | null; vocal: string | null; source: string;
} {
    const out: { instrumental: string | null; vocal: string | null; source: string } = { instrumental: null, vocal: null, source: 'none' };
    if (ancestryName === '') return out;

    for (const c of ftMusicAncestryBucketCandidates(ancestryName)) {
        if (FT_MUSIC_ANCESTRY_RESERVED_BUCKETS.includes(c)) continue;
        if (out.instrumental === null && (split[c]?.instrumental ?? 0) > 0) {
            out.instrumental = c;
            out.source = 'derived';
        }
        if (out.vocal === null) out.vocal = ftMusicVocalPartner(c, split);
        // A candidate that is ITSELF a sung bucket counts too.
        if (out.vocal === null && (split[c]?.vocal ?? 0) > 0) out.vocal = c;
    }

    // Kinship fallback — only when the NAME could not spell a live bucket at all.
    if (out.instrumental === null) {
        const kin = FT_MUSIC_ANCESTRY_KIN[ftMusicSlug(ancestryName)];
        if (kin !== undefined && (split[kin]?.instrumental ?? 0) > 0) {
            out.instrumental = kin;
            out.source = 'kin';
            if (out.vocal === null) out.vocal = ftMusicVocalPartner(kin, split);
        }
    }

    // A sung bucket with no instrumental partner is not a program — it is twenty minutes of choir.
    if (out.instrumental === null) out.vocal = null;
    return out;
}

/**
 * The creator program for an ancestry. `orderKey` seeds each slot's own shuffle
 * (`creator|<characterId|anon>|<ancestryId>`): one sitting keeps a stable running order, a different
 * character opens on different music. Each slot advances its own cursor, so a 5-track ancestry bucket
 * cycles while the 60-track general bucket walks forward.
 */
export function ftMusicCreatorProgram(ancestryName: string, orderKey: string, catalog: FtMusicCatalog): FtMusicResolved {
    const buckets = ftMusicAncestryBuckets(ancestryName, ftMusicBucketSplit(catalog));
    const pools: Record<string, FtMusicTrack[]> = {};
    const cycle: string[] = [];

    if (buckets.instrumental !== null) {
        const pool = ftMusicBucketTracks(catalog, buckets.instrumental, orderKey + '|ai', false);
        if (pool.length) { pools.ancestry_instrumental = pool; cycle.push('ancestry_instrumental'); }
    }
    if (buckets.vocal !== null) {
        // This slot EXISTS to play the sung takes.
        const pool = ftMusicBucketTracks(catalog, buckets.vocal, orderKey + '|av', true);
        if (pool.length) { pools.ancestry_vocal = pool; cycle.push('ancestry_vocal'); }
    }
    const general = ftMusicBucketTracks(catalog, FT_MUSIC_CREATOR_BUCKET, orderKey + '|g', false);
    if (general.length) { pools.general = general; cycle.push('general'); }

    const base = {
        order_key: orderKey, crossfade_ms: catalog.creator_crossfade_ms, missing: [] as string[],
        ancestry_name: ancestryName, ancestry_bucket: buckets.instrumental, vocal_bucket: buckets.vocal,
    };
    if (!cycle.length) {
        return { ...base, mode: 'none', bucket: null, tracks: [], track_count: 0, program: [], reason: 'no creator music available' };
    }

    const cursor: Record<string, number> = {};
    const tracks: FtMusicTrack[] = [];
    for (let i = 0; tracks.length < FT_MUSIC_CREATOR_PROGRAM_LEN; i++) {
        const slot = cycle[i % cycle.length] as string;
        const pool = pools[slot] as FtMusicTrack[];
        const at = cursor[slot] ?? 0;
        cursor[slot] = at + 1;
        tracks.push({
            ...(pool[at % pool.length] as FtMusicTrack),
            program_slot: slot,
            program_bucket: slot === 'general' ? FT_MUSIC_CREATOR_BUCKET
                : slot === 'ancestry_vocal' ? buckets.vocal : buckets.instrumental,
        });
    }

    return {
        ...base,
        mode: 'creator',
        bucket: buckets.instrumental ?? FT_MUSIC_CREATOR_BUCKET,
        tracks,
        track_count: tracks.length,
        program: cycle,
        reason: 'creator program: ' + cycle.join(' -> ') + (buckets.source === 'kin' ? ' (kinship bucket)' : ''),
    };
}
